/**
 * Robot Manager API Client (v3 — PR2-4 重构终态)
 *
 * 对应后端 v3 schema:
 * - RobotUnit 仅"不变属性"，可变状态在 RobotUnitSnapshot（1:1）
 * - LifecycleEvent 全事件流取代 statusChangeLogs
 * - Customer/Supplier/Location/Partner 迁到 platform-master
 * - 28 个 RobotLifecycleStage（含 6 部门前缀 + 反向边）
 * - 字典通过 platform-master/dictionaries 取
 */

import { get, post, put, del } from '@/lib/api-client';
import apiClient from '@/lib/api-client';

// ==================== Enums ====================

/**
 * 28 个 v3 stage（含部门前缀 + 终态 / 例外态）
 * 来源 backend/prisma/schema/robot_manager.prisma + 最终字段设计.md §9
 */
export type RobotLifecycleStage =
  | 'SUPPLY_PO_CREATED'
  | 'SUPPLY_IN_PRODUCTION'
  | 'SUPPLY_READY_TO_SHIP'
  | 'LOGISTICS_IN_TRANSIT'
  | 'LOGISTICS_BONDED'
  | 'LOGISTICS_CUSTOMS_CLEARED'
  | 'WAREHOUSE_RECEIVED'
  | 'WAREHOUSE_AT_W1_PDI'
  | 'WAREHOUSE_MODIFICATION'
  | 'WAREHOUSE_AT_W2'
  | 'WAREHOUSE_AT_W2_RLE'
  | 'WAREHOUSE_BRANDED_READY'
  | 'SALES_RESERVED'
  | 'SALES_PAYMENT_VALIDATED'
  | 'DELIVERY_APPROVAL'
  | 'DELIVERY_PAYMENT_COLLECTED'
  | 'DELIVERY_READY'
  | 'DELIVERY_DELIVERED'
  | 'RENTAL_ACTIVE'
  | 'AFTERSALES_TICKET'
  | 'AFTERSALES_RETURN_INITIATED'
  | 'AFTERSALES_RETURN_RECEIVED'
  | 'AFTERSALES_AT_W6'
  | 'AFTERSALES_UNDER_REPAIR'
  | 'AFTERSALES_QUOTE_APPROVAL'
  | 'AFTERSALES_REPAIRED'
  | 'CLOSED'
  | 'CANCELLED'
  | 'RETURNED';

/**
 * @deprecated v2 别名 — 新代码用 RobotLifecycleStage
 * 类型放宽为 string，让旧 STATUS_COLORS 字典（含 v2 ORDERED/IN_STOCK 等过期 key）
 * 能继续编译；运行时 v3 数据不命中 v2 key 走 fallback 灰色，不报错。
 * 这些 UI 页面在 v3 frontend 重写时彻底替换。
 */
export type RobotStatus = RobotLifecycleStage | string;

export type RobotUsageType =
  | 'SALES'
  | 'RND'
  | 'MARKETING'
  | 'CAPITAL'
  | 'PRODUCT'
  | 'TO_AGIBOT'
  | 'LAUNCH_EVENT';

export type RobotDisposalType = 'SCRAPPED' | 'SWAPPED_TO_AGIBOT' | 'CANCELLED';

export type StageDepartment =
  | 'SUPPLY'
  | 'LOGISTICS'
  | 'WAREHOUSE'
  | 'SALES'
  | 'DELIVERY'
  | 'RENTAL'
  | 'AFTERSALES'
  | 'TERMINAL';

export function stageDepartment(stage: RobotLifecycleStage): StageDepartment {
  if (stage.startsWith('SUPPLY_')) return 'SUPPLY';
  if (stage.startsWith('LOGISTICS_')) return 'LOGISTICS';
  if (stage.startsWith('WAREHOUSE_')) return 'WAREHOUSE';
  if (stage.startsWith('SALES_')) return 'SALES';
  if (stage.startsWith('DELIVERY_')) return 'DELIVERY';
  if (stage.startsWith('RENTAL_')) return 'RENTAL';
  if (stage.startsWith('AFTERSALES_')) return 'AFTERSALES';
  return 'TERMINAL';
}

export type RobotLifecycleEventType =
  | 'stage_changed'
  | 'held'
  | 'unheld'
  | 'location_moved'
  | 'sn_activated'
  | 'usage_type_changed'
  | 'label_applied'
  | 'inspection_logged'
  | 'readiness_completed'
  | 'payment_collected'
  | 'delivery_signed'
  | 'service_opened'
  | 'service_closed'
  | 'imported_from_v5'
  | 'note_added';

// ==================== Master Data (platform-master) ====================

interface EntityRef {
  id: string;
  code: string;
  name: string;
}

export interface Customer {
  id: string;
  code: string;
  name: string;
  type: 'B2B' | 'B2C' | 'INTERNAL';
  industry?: string;
  countryCode?: string;
  currencyCode?: string;
  enabled: boolean;
}

export interface Supplier {
  id: string;
  code: string;
  name: string;
  type: 'MANUFACTURER' | 'PARTS' | 'LOGISTICS' | 'SERVICE';
  countryCode?: string;
  currencyCode?: string;
  paymentTerms?: string;
  leadTimeDays?: number;
  enabled: boolean;
}

export interface Partner {
  id: string;
  code: string;
  name: string;
  role: 'SALES_AGENT' | 'MENTOR' | 'MENTEE' | 'CUSTOMS_BROKER' | 'TRAINING_PARTNER';
  countryCode?: string;
  enabled: boolean;
}

export interface Location {
  id: string;
  code: string;
  name: string;
  type: 'WAREHOUSE' | 'CUSTOMER_SITE' | 'FACTORY' | 'BONDED_ZONE' | 'IN_TRANSIT' | 'SERVICE_CENTER';
  countryCode?: string;
  address?: string;
  customerId?: string;
  enabled: boolean;
}

// ==================== Robot Manager Entities ====================

export interface RobotModel {
  id: string;
  code: string;
  name: string;
  brand?: string;
  description?: string;
  imageUrl?: string;
  enabled: boolean;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  /** @deprecated v2 计数 — v3 后端不再返回 */
  _count?: { skus?: number; robotUnits?: number };
}

export interface RobotSku {
  id: string;
  modelId: string;
  code: string;
  name: string;
  variant?: string;
  defaultPrice?: number;
  defaultCost?: number;
  currencyCode?: string;
  description?: string;
  enabled: boolean;
  metadata?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
  /** joined relation */
  model?: EntityRef;
  /** @deprecated v2 计数字段 */
  _count?: { robotUnits?: number };
}

export interface RobotSystemConfigEntry {
  id: string;
  key: string;
  value: any;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== Core: RobotUnit + Snapshot + Event ====================

/** v3: 当前状态物化（1:1 RobotUnit） */
export interface RobotUnitSnapshot {
  robotUnitId: string;
  currentStage: RobotLifecycleStage;
  isHeld: boolean;
  holdReason?: string;
  currentLocationId?: string;
  currentCustomerId?: string;
  currentSalesOrderId?: string;
  currentDeliveryRequestId?: string;
  currentSpecialistId?: string;
  physicalProductStatus?: 'STORED' | 'IN_TRANSIT' | 'DELIVERED' | 'IN_SERVICE' | 'RETIRED';
  daysReadyForDelivery?: number;
  warrantyStatus?: 'INACTIVE' | 'ACTIVE' | 'EXPIRED' | 'VOIDED';
  lastEventId?: string;
  lastEventAt?: string;
  derivedAt: string;
  version: number;
}

export interface RobotLifecycleEvent {
  id: string;
  robotUnitId: string;
  eventType: RobotLifecycleEventType;
  fromStage?: RobotLifecycleStage;
  toStage?: RobotLifecycleStage;
  fromLocationId?: string;
  toLocationId?: string;
  actorUserId?: string;
  customerId?: string;
  payload: Record<string, any>;
  notes?: string;
  occurredAt: string;
  createdAt: string;
  /** @deprecated v2 别名，等于 fromStage */
  fromStatus?: RobotLifecycleStage | null;
  /** @deprecated v2 别名，等于 toStage */
  toStatus?: RobotLifecycleStage;
  /** @deprecated v2 别名，等于 createdById */
  changedBy?: string;
  /** @deprecated v2 别名，等于 occurredAt */
  changedAt?: string;
  /** @deprecated v2 别名，等于 notes */
  remark?: string;
  changedByUser?: { id: string; displayName: string; username: string } | null;
}

/** v2 别名：兼容旧 UI */
export type StatusChangeLog = RobotLifecycleEvent;

export interface RobotUnit {
  id: string;
  organizationId: string;
  version: number;

  // 身份
  ffsn: string;
  ffsnDisplay?: string;
  placeholderSnOrig?: string;
  supplierSn?: string;

  // 物料 / 用途（不变）
  modelId: string;
  skuId: string;
  usageType: RobotUsageType;
  purchaseOrderId?: string;
  purchaseOrderLineId?: string;
  originalSupplierId?: string;
  manufactureDate?: string;

  // 退役
  retiredAt?: string;
  disposalType?: RobotDisposalType;
  disposalNotes?: string;

  // SAP anchor
  sapMaterialNo?: string;

  metadata: Record<string, any>;

  // joined
  model?: EntityRef;
  sku?: EntityRef & { variant?: string; model?: EntityRef };
  snapshot?: RobotUnitSnapshot;
  events?: RobotLifecycleEvent[];

  // v2 别名 — getter 派生自 snapshot
  /** @deprecated 用 snapshot.currentStage */
  currentStatus?: string;
  /** @deprecated 用 events */
  serviceRecords?: any[];
  /** @deprecated 用 events 过滤 stage_changed */
  statusChangeLogs?: RobotLifecycleEvent[];
  /** @deprecated v2 supplier/customer/location relation — v3 走 snapshot */
  supplier?: EntityRef;
  customer?: EntityRef;
  location?: EntityRef & { typeCode?: string; customer?: EntityRef };
  /** @deprecated v2 报告字段 */
  grossMargin?: number | null;

  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface RobotListResponse {
  items: RobotUnit[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// ==================== DTOs ====================

export interface ChangeStageDto {
  toStage: RobotLifecycleStage;
  reason?: string;
  metadata?: Record<string, any>;
  version?: number;
}

export interface BulkChangeStageDto {
  robotUnitIds: string[];
  toStage: RobotLifecycleStage;
  reason?: string;
}

/** 占位 SN 激活 DTO（v3：07 RECEIVED 扫供应商物理标签） */
export interface ActivateSnDto {
  /** 扫码得到的 Supplier SN（必填） */
  supplierSn: string;
  /** 可选：手动指定正式 FFSN（如从 SAP 同步），不传则后端生成 */
  ffsn?: string;
  /** 可选：激活后同步推进到的 stage（默认不推进） */
  advanceTo?: RobotLifecycleStage;
  reason?: string;
}

/** @deprecated v2 别名 */
export type StatusChangeDto = ChangeStageDto;
/** @deprecated v2 别名 */
export type BulkStatusChangeDto = BulkChangeStageDto;

export interface HoldStageDto {
  holdReason: string;
}

export interface MoveLocationDto {
  toLocationId: string;
  reason?: string;
}

// ==================== Reports ====================

export interface InventoryReport {
  total: number;
  inWarehouse: number;
  available: number;
  inTransit: number;
  byStage: Partial<Record<RobotLifecycleStage, number>>;
  /** @deprecated v2 别名 — 用 byStage */
  byStatus?: Partial<Record<string, number>>;
  /** @deprecated v2 别名 — 用 inWarehouse */
  physical?: number;
}

export interface SalesReport {
  totalDelivered: number;
  totalRevenue: number;
  customerBreakdown: Array<{ customerId?: string; customerName?: string; count: number; revenue: number }>;
  monthlyTrend?: Array<{ month: string; count: number; revenue: number }>;
}

export interface FinanceReport {
  unitCount: number;
  revenue: number;
  totalCost: number;
  margin: number;
}

// ==================== Robot API ====================

export interface RobotListQuery {
  search?: string;
  currentStage?: string | string[];
  modelId?: string;
  skuId?: string;
  purchaseOrderId?: string;
  currentLocationId?: string;
  currentCustomerId?: string;
  isHeld?: boolean;
  includeDeleted?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** @deprecated v2 别名，自动映射到 currentStage */
  currentStatus?: string | string[];
  /** @deprecated v2 别名 */
  supplierId?: string;
  /** @deprecated v2 别名 */
  customerId?: string;
  /** @deprecated v2 别名 */
  locationId?: string;
}

export const robotApi = {
  list: (params?: RobotListQuery): Promise<RobotListResponse> => {
    // v2 兼容：currentStatus → currentStage
    const mapped: any = { ...params };
    if (mapped.currentStatus !== undefined && mapped.currentStage === undefined) {
      mapped.currentStage = mapped.currentStatus;
      delete mapped.currentStatus;
    }
    return get('/robot-manager', { params: mapped });
  },

  get: (id: string): Promise<RobotUnit> => get(`/robot-manager/${id}`),

  /** 扫码 deep link：通过 ffsn 拿 id，再跳到详情页 */
  getByFfsn: (ffsn: string): Promise<{ id: string; ffsn: string }> =>
    get(`/robot-manager/by-ffsn/${encodeURIComponent(ffsn)}`),

  create: (data: Partial<RobotUnit>): Promise<RobotUnit> => post('/robot-manager', data),

  update: (id: string, data: Partial<RobotUnit>): Promise<RobotUnit> =>
    put(`/robot-manager/${id}`, data),

  delete: (id: string): Promise<void> => del(`/robot-manager/${id}`),

  // v3 lifecycle endpoints
  changeStage: (id: string, data: ChangeStageDto): Promise<RobotUnit> =>
    post(`/robot-manager/${id}/change-stage`, data),

  /** 占位 SN 激活（v3：07 RECEIVED 扫码 → 占位 SN 替换为正式 FFSN，原值保留 placeholderSnOrig） */
  activateSn: (id: string, data: ActivateSnDto): Promise<RobotUnit> =>
    post(`/robot-manager/${id}/activate-sn`, data),

  bulkChangeStage: (
    data: BulkChangeStageDto,
  ): Promise<{ success: string[]; failed: Array<{ id: string; reason: string }> }> =>
    post('/robot-manager/bulk-change-stage', data),

  hold: (id: string, data: HoldStageDto): Promise<RobotLifecycleEvent> =>
    post(`/robot-manager/${id}/hold`, data),

  unhold: (id: string): Promise<RobotLifecycleEvent> =>
    post(`/robot-manager/${id}/unhold`, {}),

  moveLocation: (id: string, data: MoveLocationDto): Promise<RobotLifecycleEvent> =>
    post(`/robot-manager/${id}/move-location`, data),

  listEvents: (id: string, limit?: number): Promise<RobotLifecycleEvent[]> =>
    get(`/robot-manager/${id}/events`, { params: limit ? { limit } : {} }),

  allowedNextStages: (id: string): Promise<RobotLifecycleStage[]> =>
    get(`/robot-manager/${id}/allowed-next-stages`),

  // v2 别名
  /** @deprecated 用 changeStage（targetStatus 类型放宽给 v2 调用方） */
  changeStatus: (id: string, data: { targetStatus: string; remark?: string; locationId?: string }) =>
    post(`/robot-manager/${id}/change-stage`, { toStage: data.targetStatus, reason: data.remark }),

  /** @deprecated 用 bulkChangeStage */
  bulkStatusChange: (data: { robotUnitIds: string[]; targetStatus: string; remark?: string }) =>
    post('/robot-manager/bulk-change-stage', {
      robotUnitIds: data.robotUnitIds,
      toStage: data.targetStatus,
      reason: data.remark,
    }),

  /**
   * @deprecated v2 section-level update — v3 改用：
   *   - 不变属性走 PUT /robot-manager/:id (update)
   *   - 状态切换走 POST /robot-manager/:id/change-stage
   *   - 客户/位置/付款 通过对应业务表 API 触发事件
   * 此 stub fallback 到全量 update 让旧 UI 编译通过。
   */
  updateSection: (
    id: string,
    _section: RobotSection,
    data: Partial<RobotUnit> & { version?: number },
  ): Promise<RobotUnit> => put(`/robot-manager/${id}`, data),
};

// Inspection records (W1 PDI 等触发 functionTest guard 的检查记录)
export interface InspectionRecord {
  id: string;
  robotUnitId: string;
  inspectionNo: number;
  inspectedAt: string;
  inspectorId?: string | null;
  issue?: string | null;
  issueTagCode?: string | null;
  resolvedAt?: string | null;
  resolvedById?: string | null;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  createdById: string;
}

export interface AddInspectionDto {
  /// 可选；不传则 backend 在事务内派生（max+1）。
  /// 前端切勿用本地 unit.inspections.length 计算 —— RobotUnit list/detail 默认不 join，
  /// 永远 = 0 → 重测会撞 (robotUnitId, inspectionNo) unique。
  inspectionNo?: number;
  inspectorId?: string;
  issue?: string;
  issueTagCode?: string;
  resolvedAt?: string;
  resolvedById?: string;
}

export const inspectionApi = {
  list: (robotId: string): Promise<InspectionRecord[]> =>
    get(`/robot-manager/${robotId}/inspections`),
  add: (robotId: string, dto: AddInspectionDto): Promise<InspectionRecord> =>
    post(`/robot-manager/${robotId}/inspections`, dto),
};

// Quality labels (W2 MODIFICATION → BRANDED_READY 的 checkConversionValidated guard)
export interface UpsertQualityLabelDto {
  labelTypeCode: string;
  status?: 'PENDING' | 'APPLIED' | 'VERIFIED';
  appliedById?: string;
  verifiedById?: string;
  photoAttachmentId?: string;
}

export const qualityLabelApi = {
  list: (robotId: string): Promise<any[]> =>
    get(`/robot-manager/${robotId}/quality-labels`),
  upsert: (robotId: string, dto: UpsertQualityLabelDto): Promise<any> =>
    put(`/robot-manager/${robotId}/quality-labels`, dto),
};

// Package readiness (10 件配件清点)
export interface UpsertReadinessDto {
  specialistId?: string;
  physicalProductStatus?: 'OK' | 'DAMAGED' | 'REJECTED';
  hasRobot?: boolean;
  hasBattery?: boolean;
  hasRemote?: boolean;
  hasUsaPowerCable?: boolean;
  hasCnCableWithAdapter?: boolean;
  hasPowerSupply?: boolean;
  hasChargingDock?: boolean;
  hasFootPads?: boolean;
  hasToolsKit?: boolean;
  hasPaperwork?: boolean;
  completedAt?: string;
}

export const readinessApi = {
  get: (robotId: string): Promise<any> =>
    get(`/robot-manager/${robotId}/readiness`),
  upsert: (robotId: string, dto: UpsertReadinessDto): Promise<any> =>
    put(`/robot-manager/${robotId}/readiness`, dto),
};

// Delivery fulfillment (DELIVERY_READY → DELIVERED 的 checkDeliveryValidation + checkPGIReady guard)
export interface CreateFulfillmentDto {
  deliveryRequestId: string;
  robotUnitId: string;
  deliveredAt: string;
  signedAt?: string;
  signedFormStatus?: 'PENDING' | 'SIGNED' | 'WAIVED';
  acceptanceFormStatus?: 'PENDING' | 'SIGNED' | 'WAIVED';
  sapPgiDocNo?: string;
  cost?: number;
}

export const fulfillmentApi = {
  create: (dto: CreateFulfillmentDto): Promise<any> =>
    post('/robot-manager/delivery-fulfillments', dto),
  update: (id: string, dto: Partial<CreateFulfillmentDto>): Promise<any> =>
    put(`/robot-manager/delivery-fulfillments/${id}`, dto),
};

export const reportApi = {
  inventory: (): Promise<InventoryReport> => get('/robot-manager/reports/inventory'),
  sales: (): Promise<SalesReport> => get('/robot-manager/reports/sales'),
  finance: (): Promise<FinanceReport> => get('/robot-manager/reports/finance'),
};

// ==================== Platform Master entity APIs ====================

function makeMasterApi<T, Create = Partial<T>, Update = Partial<T>>(resource: string) {
  return {
    list: (params?: any): Promise<T[]> => get(`/platform-master/${resource}`, { params }),
    get: (id: string): Promise<T> => get(`/platform-master/${resource}/${id}`),
    create: (data: Create): Promise<T> => post(`/platform-master/${resource}`, data),
    update: (id: string, data: Update): Promise<T> =>
      put(`/platform-master/${resource}/${id}`, data),
    delete: (id: string): Promise<{ id: string; deleted: boolean }> =>
      del(`/platform-master/${resource}/${id}`),
    restore: (id: string): Promise<T> => post(`/platform-master/${resource}/${id}/restore`, {}),
  };
}

export const customerApi = makeMasterApi<Customer>('customers');
export const supplierApi = makeMasterApi<Supplier>('suppliers');
export const partnerApi = makeMasterApi<Partner>('partners');
export const locationApi = makeMasterApi<Location>('locations');

// Admin endpoints (Model/Sku/Config 还在 robot-manager schema)
function makeAdminEntityApi<T>(resource: string) {
  return {
    list: (params?: any): Promise<T[]> => get(`/robot-manager/admin/${resource}`, { params }),
    get: (id: string): Promise<T> => get(`/robot-manager/admin/${resource}/${id}`),
    create: (data: Partial<T>): Promise<T> => post(`/robot-manager/admin/${resource}`, data),
    update: (id: string, data: Partial<T>): Promise<T> =>
      put(`/robot-manager/admin/${resource}/${id}`, data),
    delete: (id: string): Promise<void> => del(`/robot-manager/admin/${resource}/${id}`),
  };
}

export const modelApi = makeAdminEntityApi<RobotModel>('models');
export const skuApi = makeAdminEntityApi<RobotSku>('skus');

// Config (key-value)
export const configApi = {
  list: (): Promise<RobotSystemConfigEntry[]> => get('/robot-manager/admin/config'),
  get: (key: string): Promise<{ key: string; value: any }> =>
    get(`/robot-manager/admin/config/${key}`),
  set: (key: string, value: any, description?: string) =>
    put(`/robot-manager/admin/config/${key}`, { value, description }),
};

// ==================== L3 业务表 ====================

export interface PurchaseOrder {
  id: string;
  poNo: string;
  sapPoNo?: string;
  supplierId: string;
  currencyCode: string;
  totalAmount: number;
  status: 'DRAFT' | 'ORDERED' | 'PARTIAL_RECEIVED' | 'RECEIVED' | 'CLOSED' | 'CANCELLED';
  orderedAt: string;
  expectedAt?: string;
  closedAt?: string;
  notes?: string;
  createdAt: string;
  lines?: PurchaseOrderLine[];
  _count?: { robotUnits?: number };
}
export interface PurchaseOrderLine {
  id: string;
  lineNo: number;
  skuId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  currencyCode: string;
  defaultUsageType?: RobotUsageType;
  expectedAt?: string;
}

export interface SalesOrder {
  id: string;
  soNo: string;
  d365SoId?: string;
  customerId: string;
  salesPersonId?: string;
  mentorId?: string;
  menteeId?: string;
  currencyCode: string;
  totalAmount: number;
  contractStatus: 'DRAFT' | 'SIGNED' | 'VOID';
  status: 'RESERVED' | 'PENDING' | 'APPROVED' | 'FULFILLING' | 'COMPLETED' | 'CANCELLED';
  signedAt?: string;
  notes?: string;
  createdAt: string;
  lines?: SalesOrderLine[];
}
export interface SalesOrderLine {
  id: string;
  lineNo: number;
  lineType: 'HARDWARE' | 'SOFTWARE' | 'COCREATION';
  unitPrice: number;
  discount?: number;
  netAmount: number;
  currencyCode: string;
  robotUnitId?: string;
}

export interface DeliveryRequest {
  id: string;
  deliveryNo: string;
  salesOrderId: string;
  customerId: string;
  requestType: 'WILL_CALL' | 'THREE_PL' | 'WHITE_GLOVE';
  expectedDate: string;
  status: 'REQUESTED' | 'APPROVED' | 'FULFILLING' | 'COMPLETED' | 'CANCELLED';
  notes?: string;
  createdAt: string;
}

export interface PaymentRecord {
  id: string;
  paymentNo: string;
  relatedType: 'SALES_ORDER' | 'PURCHASE_ORDER' | 'RENTAL_AGREEMENT';
  relatedId: string;
  robotUnitId?: string;
  direction: 'INBOUND' | 'OUTBOUND';
  amount: number;
  currencyCode: string;
  paymentMethod?: string;
  paymentStatus?: 'NOT_PAID_YET' | 'PARTIAL' | 'PAID' | 'CANCELLED' | 'DRAFT';
  paidAt?: string;
  createdAt: string;
}

export interface ServiceTicket {
  id: string;
  ticketNo: string;
  robotUnitId: string;
  customerId?: string;
  issueTypeCode: string;
  severity: 'P0' | 'P1' | 'P2' | 'P3';
  status: 'NEW' | 'IN_PROGRESS' | 'AWAITING_CUSTOMER' | 'RESOLVED' | 'CLOSED';
  serviceRecord?: string;
  openedAt: string;
  closedAt?: string;
}

export interface RentalAgreement {
  id: string;
  robotUnitId: string;
  customerId: string;
  startAt: string;
  endAt: string;
  periodMonths: number;
  monthlyRate: number;
  currencyCode: string;
  status: 'ACTIVE' | 'EXPIRED' | 'TERMINATED';
  createdAt: string;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

function makeL3Api<T>(resource: string) {
  return {
    list: (params?: any): Promise<PaginatedResponse<T>> =>
      get(`/robot-manager/${resource}`, { params }),
    get: (id: string): Promise<T> => get(`/robot-manager/${resource}/${id}`),
    create: (data: any): Promise<T> => post(`/robot-manager/${resource}`, data),
    update: (id: string, data: any): Promise<T> => put(`/robot-manager/${resource}/${id}`, data),
    delete: (id: string): Promise<void> => del(`/robot-manager/${resource}/${id}`),
  };
}

export const purchaseOrderApi = makeL3Api<PurchaseOrder>('purchase-orders');
export const salesOrderApi = makeL3Api<SalesOrder>('sales-orders');
export const deliveryRequestApi = makeL3Api<DeliveryRequest>('delivery-requests');
export const paymentApi = {
  ...makeL3Api<PaymentRecord>('payments'),
  markPaid: (id: string, paidAt: string): Promise<PaymentRecord> =>
    post(`/robot-manager/payments/${id}/mark-paid`, { paidAt }),
};
export const serviceTicketApi = {
  ...makeL3Api<ServiceTicket>('service-tickets'),
  addActivity: (id: string, activityType: string, payload?: any) =>
    post(`/robot-manager/service-tickets/${id}/activities`, { activityType, payload }),
};
export const rentalApi = {
  ...makeL3Api<RentalAgreement>('rentals'),
  terminate: (id: string): Promise<RentalAgreement> =>
    post(`/robot-manager/rentals/${id}/terminate`, {}),
};

// ==================== Search ====================

export interface SearchHit {
  type: 'unit' | 'model' | 'sku' | 'supplier' | 'customer' | 'location';
  id: string;
  title: string;
  subtitle?: string;
  href: string;
}

export interface GlobalSearchResult {
  units: SearchHit[];
  models: SearchHit[];
  skus: SearchHit[];
  suppliers: SearchHit[];
  customers: SearchHit[];
  locations: SearchHit[];
}

export const searchApi = {
  search: (q: string): Promise<GlobalSearchResult> =>
    get('/robot-manager/search', { params: { q } }),
};

// ==================== Dictionary (platform-master) ====================

export interface DictionaryItem {
  id: string;
  category: string;
  code: string;
  labelEn: string;
  labelZh?: string;
  sortOrder: number;
  enabled: boolean;
  metadata: Record<string, any>;
}

// ==================== v2 兼容 stub (待 frontend 完全切 v3 后删) ====================

/** @deprecated v2 section 概念 — v3 用事件流取代 */
export type RobotSection = 'identity' | 'supply-chain' | 'sales' | 'finance' | 'after-sales' | 'compliance';

/** @deprecated v2 RobotAttachment 已迁到 platform_master.Attachment（多态）*/
export interface RobotAttachment {
  id: string;
  robotUnitId: string;
  filename: string;
  mimeType: string;
  size: number;
  storagePath: string;
  uploadedBy: string;
  uploadedAt: string;
}

/** @deprecated v2 ServiceRecord 已被 ServiceTicket + Lifecycle Event 取代 */
export interface ServiceRecord {
  id: string;
  robotUnitId: string;
  serviceTypeCode: string;
  description: string;
  resolution?: string;
  serviceDate: string;
  completedDate?: string;
  customerFeedback?: string;
  createdBy: string;
  createdAt: string;
}

/** @deprecated 用 dictionaryApi */
export type RobotOptionCategory = string;

/** @deprecated FieldDef.options 中的字典项 */
export interface RobotFieldOption {
  code: string;
  label: string;
  labelEn?: string;
  description?: string;
}

/** @deprecated v2 RobotCustomer，迁到 Customer */
export type RobotCustomer = Customer;
/** @deprecated v2 RobotSupplier，迁到 Supplier */
export type RobotSupplier = Supplier;
/** @deprecated v2 RobotLocation，迁到 Location */
export type RobotLocation = Location;

/** @deprecated FieldDef 已移除，返回空数组 */
export type RobotFieldScope = 'unit' | 'service_record' | 'location';

/** @deprecated v2 字段分组 — v3 由部门前缀 stage 取代 */
export type RobotFieldGroup =
  | 'identity'
  | 'supply-chain'
  | 'sales'
  | 'finance'
  | 'after-sales'
  | 'compliance';
export interface RobotFieldDef {
  id: string;
  scope: RobotFieldScope;
  key: string;
  labelEn: string;
  label?: string;
  description?: string;
  type: string;
  group?: string;
  sortOrder: number;
  required: boolean;
  defaultValue?: string;
  options: any[];
  validation?: Record<string, any>;
  showInList: boolean;
  indexed: boolean;
  enabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** @deprecated FieldDef 已移除，返回空 */
export const fieldDefApi = {
  list: (_params?: any): Promise<RobotFieldDef[]> => Promise.resolve([]),
  listByScope: (_scope: RobotFieldScope, _enabledOnly = true): Promise<RobotFieldDef[]> =>
    Promise.resolve([]),
  get: (_id: string): Promise<RobotFieldDef> => Promise.reject(new Error('FieldDef removed in v3')),
  create: (_data: any): Promise<RobotFieldDef> => Promise.reject(new Error('FieldDef removed in v3')),
  update: (_id: string, _data: any): Promise<RobotFieldDef> =>
    Promise.reject(new Error('FieldDef removed in v3')),
  delete: (_id: string): Promise<void> => Promise.reject(new Error('FieldDef removed in v3')),
};

/** @deprecated Excel 导入导出在 v3 PR5 重做 */
export const excelApi = {
  export: (_params?: any): Promise<Blob> =>
    Promise.reject(new Error('Excel export v3 待 PR5 重做')),
  importFile: (_file: File): Promise<any> =>
    Promise.reject(new Error('Excel import v3 待 PR5 重做')),
  preview: (_file: File): Promise<any> =>
    Promise.reject(new Error('Excel preview v3 待 PR5 重做')),
};

/** @deprecated Excel 实体导入在 v3 PR5 重做 */
export type EntityResource = 'models' | 'skus';
export const entityExcelApi = {
  export: (_resource: EntityResource, _params?: any): Promise<Blob> =>
    Promise.reject(new Error('Entity excel v3 待 PR5 重做')),
  importFile: (
    _resource: EntityResource,
    _file: File,
    _mapping?: Record<string, string | null>,
    _conflictStrategy?: 'skip' | 'update' | 'abort',
  ): Promise<any> => Promise.reject(new Error('Entity excel v3 待 PR5 重做')),
  preview: (_resource: EntityResource, _file: File): Promise<any> =>
    Promise.reject(new Error('Entity excel v3 待 PR5 重做')),
  template: (_resource: EntityResource): Promise<Blob> =>
    Promise.reject(new Error('Entity excel v3 待 PR5 重做')),
};

/** @deprecated 系统字典查询 — v3 改用 dictionaryApi */
export const loadSystemDict = async (
  _scopeOrCategory: string,
  _categoryOrKey?: string,
): Promise<Array<{ code: string; label: string }>> => {
  return [];
};

export const attachmentApi = {
  list: (_robotId: string): Promise<any[]> => Promise.resolve([]),
  upload: (_robotId: string, _file: File): Promise<any> =>
    Promise.reject(new Error('Attachment 已迁到 platform-master，v3 frontend 待重做')),
  remove: (_id: string): Promise<void> =>
    Promise.reject(new Error('Attachment 已迁到 platform-master')),
  delete: (_robotId: string, _attachmentId: string): Promise<void> =>
    Promise.reject(new Error('Attachment 已迁到 platform-master')),
  download: (_id: string): Promise<Blob> =>
    Promise.reject(new Error('Attachment 已迁到 platform-master')),
  downloadUrl: (_id: string): string => '#v3-attachment-disabled',
};

export const dictionaryApi = {
  listByCategory: (category: string): Promise<DictionaryItem[]> =>
    get('/platform-master/dictionaries', { params: { category } }),
  listCategories: (): Promise<string[]> => get('/platform-master/dictionaries/categories'),
  get: (category: string, code: string): Promise<DictionaryItem> =>
    get(`/platform-master/dictionaries/${category}/${code}`),
};

// ==================== Currency (platform-master) ====================

export interface CurrencyItem {
  id: string;
  code: string;
  name: string;
  symbol?: string | null;
  decimals: number;
  enabled: boolean;
  organizationId?: string | null;
  createdAt: string;
  updatedAt: string;
  createdById?: string | null;
}

export interface CreateCurrencyDto {
  code: string;
  name: string;
  symbol?: string;
  decimals?: number;
  enabled?: boolean;
}

export interface UpdateCurrencyDto {
  name?: string;
  symbol?: string;
  decimals?: number;
  enabled?: boolean;
}

export const currencyApi = {
  list: (opts?: { enabledOnly?: boolean }): Promise<CurrencyItem[]> =>
    get('/platform-master/currencies', {
      params: { enabledOnly: opts?.enabledOnly === false ? 'false' : 'true' },
    }),
  get: (code: string): Promise<CurrencyItem> =>
    get(`/platform-master/currencies/${code}`),
  create: (dto: CreateCurrencyDto): Promise<CurrencyItem> =>
    post('/platform-master/currencies', dto),
  update: (code: string, dto: UpdateCurrencyDto): Promise<CurrencyItem> =>
    put(`/platform-master/currencies/${code}`, dto),
  disable: (code: string): Promise<CurrencyItem> =>
    del(`/platform-master/currencies/${code}`) as unknown as Promise<CurrencyItem>,
  enable: (code: string): Promise<CurrencyItem> =>
    post(`/platform-master/currencies/${code}/enable`, {}),
};
